import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingDown, Phone, Users, Layers, AlertTriangle, Send } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { PageHeader, StatsCard, StatsGrid, EmptyState, AppButton } from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import DataTable from "../Common/DataTable";
import NotifyGuardiansModal from "./NotifyGuardiansModal";
import "./attendance.scss";

/**
 * LowAttendancePage — students below an attendance threshold across all ongoing
 * batches (or a chosen batch), using the expected-day denominator. Lowest first,
 * with contact + guardian details for follow-up.
 *
 * Route: /attendance/low-attendance
 */
const LowAttendancePage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();

  const [threshold, setThreshold] = useState(75);
  const [batchId, setBatchId] = useState("");
  const [batches, setBatches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Batch options (scoped to the global branch).
  useEffect(() => {
    apiClient.get("/batches/list?limit=200&fields=name,status")
      .then(({ data: d }) => setBatches((d.batches || []).filter((b) => b.status === "Ongoing")))
      .catch(() => { /* ignore */ });
  }, [selectedBranchId]);

  const fetchLow = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ threshold: String(threshold) });
      if (batchId) params.set("batchId", batchId);
      const { data: d } = await apiClient.get(`/attendance/low-attendance?${params.toString()}`);
      setData(d);
    } catch {
      setToast({ show: true, message: "Failed to load low-attendance report.", type: "danger" });
      setData(null);
    }
    setLoading(false);
    // selectedBranchId re-fetches on global branch switch (X-Branch-Id header).
  }, [threshold, batchId, selectedBranchId]);

  useEffect(() => { fetchLow(); }, [fetchLow]);

  const summary = data?.summary || {};
  const students = data?.students || [];

  return (
    <div className="attendance-page sl-page">
      <PageHeader
        backButton
        onBack={() => navigate("/attendance")}
        icon={TrendingDown}
        title="Low Attendance"
        description="Students below the attendance threshold across ongoing batches"
        actions={
          students.length > 0 ? (
            <AppButton variant="primary" size="sm" onClick={() => setNotifyOpen(true)}>
              <Send size={15} /> Notify Guardians
            </AppButton>
          ) : null
        }
      />

      {/* Filters */}
      <div className="attendance-filters mb-3">
        <div className="attendance-filter-item">
          <label>Below %</label>
          <input
            type="number"
            min={1}
            max={100}
            className="form-control form-control-sm"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            style={{ width: 90 }}
          />
        </div>
        <div className="attendance-filter-item">
          <label>Batch</label>
          <select
            className="form-select form-select-sm"
            value={batchId}
            onChange={(e) => setBatchId(e.target.value)}
          >
            <option value="">All Ongoing Batches</option>
            {batches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div className="attendance-filter-item attendance-filter-item--action">
          <AppButton variant="ghost" size="sm" onClick={() => { setThreshold(75); setBatchId(""); }}>
            Reset
          </AppButton>
        </div>
      </div>

      {loading ? (
        <Shimmer height={48} count={6} />
      ) : (
        <>
          {/* Summary */}
          <StatsGrid cols={3} className="attendance-analytics-stats mb-3">
            <StatsCard title="Batches Scanned" value={summary.batchesScanned || 0} icon={Layers} iconColor="blue" />
            <StatsCard title="Students Scanned" value={summary.studentsScanned || 0} icon={Users} iconColor="emerald" />
            <StatsCard title={`Below ${data?.threshold ?? threshold}%`} value={summary.studentsBelow || 0} icon={AlertTriangle} iconColor="amber" />
          </StatsGrid>

          {students.length === 0 ? (
            <EmptyState
              icon={TrendingDown}
              title="No students below threshold"
              description="Every scanned student meets the attendance threshold for the selected scope."
            />
          ) : (
            <DataTable
              data={students}
              columns={[
                { key: "name", label: "Student", render: (s) => s.name },
                { key: "batch", label: "Batch", render: (s) => s.batchName },
                { key: "branch", label: "Branch", render: (s) => s.branchName || "-" },
                { key: "present", label: "Present / Held", render: (s) => `${s.present} / ${s.held}` },
                {
                  key: "pct",
                  label: "Attendance",
                  render: (s) => (
                    <span className={`att-pct ${s.percentage >= 50 ? "att-pct--warn" : "att-pct--bad"}`}>
                      {s.percentage}%
                    </span>
                  ),
                },
                {
                  key: "contact",
                  label: "Contact",
                  render: (s) =>
                    s.mobile ? (
                      <a className="attendance-contact" href={`tel:${s.mobile}`}>
                        <Phone size={13} /> {s.mobile}
                      </a>
                    ) : "-",
                },
                {
                  key: "guardian",
                  label: "Guardian",
                  render: (s) =>
                    s.guardianMobile ? (
                      <a className="attendance-contact" href={`tel:${s.guardianMobile}`}>
                        <Phone size={13} /> {s.guardianName || s.guardianMobile}
                      </a>
                    ) : "-",
                },
              ]}
            />
          )}
        </>
      )}

      <NotifyGuardiansModal
        show={notifyOpen}
        onHide={() => setNotifyOpen(false)}
        endpoint="/attendance/notify-low-attendance"
        payload={{ threshold, batchId: batchId || undefined }}
        count={students.length}
        title="Notify Low-Attendance Guardians"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default LowAttendancePage;
